import { Component, Input, OnInit } from '@angular/core';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import Employee from 'src/app/model/product/employee';
import { SubProject } from 'src/app/model/project/sub-project';
import {
  AssignSubProjectResult,
  AssignSubProjectToTourControllerRequestService,
} from 'src/app/pages/immo-ip/tour/tour-page/service/assign-sub-project-to-tour-controller-request.service';
import { IPInstallerListControllerRequestService } from 'src/app/pages/immo-ip/tour/tour-page/service/ip-installer-list-controller-request.service';
import {
  ProjectDisplayObservableService,
  TourConfigurationChangedEvent,
  TourUnassignedAsSideEffectEvent,
} from 'src/app/services/project-display-observable.service';
import { SubProjectTourInfo } from './helper/model/sub-project-tour-info';
import { SubProjectTourInfoControllerRequestService } from './service/sub-project-tour-info-controller-request.service';
import * as moment from 'moment';
import { TourItem } from 'src/app/model/tour/tour-item';
import { SnackService } from 'src/app/services/snack.service';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import { Observable } from 'rxjs';
import LoggedAction from '../../../../model/logged-action';
import { AuthService } from '../../../../services/auth.service';

@Component({
  selector: 'ip-board-tour-selector',
  templateUrl: './ip-board-tour-selector.component.html',
  styleUrls: ['./ip-board-tour-selector.component.scss'],
})
export class IPBoardTourSelectorComponent
  extends SubscriptionBasedComponent
  implements OnInit
{
  @Input()
  subProject: SubProject;
  @Input()
  tourUnassigned: Observable<void>;

  loading: boolean = true;

  placerOptions: Employee[];

  placer: Employee;
  date: Date;
  locked: boolean;
  today = new Date();

  constructor(
    private subProjectTourInfoControllerRequestService: SubProjectTourInfoControllerRequestService,
    private ipInstallerListControllerRequestService: IPInstallerListControllerRequestService,
    private assignSubProjectToTourControllerRequestService: AssignSubProjectToTourControllerRequestService,
    private projectDisplayObservableService: ProjectDisplayObservableService,
    private snackService: SnackService,
    private authService: AuthService
  ) {
    super();
  }

  ngOnInit(): void {
    const installerListPromise = this.ipInstallerListControllerRequestService
      .IPInstallerList()
      .then((placers: Employee[]) => (this.placerOptions = placers));
    const tourInfoPromise = this.subProjectTourInfoControllerRequestService
      .subProjectTourInfo(this.subProject.id)
      .then((subProjectTourInfo: SubProjectTourInfo) => {
        if (subProjectTourInfo != null && subProjectTourInfo.assignedToTour) {
          this.placer = subProjectTourInfo.placer;
          this.date = subProjectTourInfo.date;
          this.locked = subProjectTourInfo.locked;
          this.projectDisplayObservableService
            .getTourConfigurationChangedSubscribable()
            .next({
              assignedToTour: true,
              subProjectId: this.subProject.id,
              placer: this.placer,
              date: this.date,
              locked: this.locked,
            });
        } else if (subProjectTourInfo != null) {
          this.projectDisplayObservableService
            .getTourConfigurationChangedSubscribable()
            .next({
              assignedToTour: false,
              subProjectId: this.subProject.id,
              placer: null,
              date: null,
              locked: false,
            });
        }
      });
    Promise.all([installerListPromise, tourInfoPromise]).finally(
      () => (this.loading = false)
    );

    this.initializeSubscriptions();
  }

  private initializeSubscriptions(): void {
    if (this.tourUnassigned != null) {
      this.addSubscription(this.tourUnassigned, () =>
        this.unsetTourConfiguration()
      );
    }

    this.addSubscription(
      this.projectDisplayObservableService.getTourUnassignedAsSideEffectSubscribable(),
      (event: TourUnassignedAsSideEffectEvent) => {
        if (event.subProjectId === this.subProject.id) {
          this.unsetTourConfiguration();
        }
      }
    );

    this.addSubscription(
      this.projectDisplayObservableService.getTourConfigurationChangedSubscribable(),
      (event: TourConfigurationChangedEvent) => {
        if (
          event.subProjectId === this.subProject.id &&
          this.locked != event.locked
        ) {
          this.locked = event.locked;
        }
      }
    );
  }

  private unsetTourConfiguration(): void {
    this.placer = null;
    this.date = null;
    this.locked = null;
  }

  tourConfigurationChanged(): void {
    if (this.placer != null && this.date != null) {
      this.doSaveTourConfiguration(true);
    }
  }

  unassignFromTour(): void {
    this.doSaveTourConfiguration(false);
  }

  private async doSaveTourConfiguration(
    assignToCurrentSelection: boolean
  ): Promise<void> {
    if (
      assignToCurrentSelection &&
      (this.placer == null || this.date == null)
    ) {
      this.snackService.showError(
        'Kan niet toevoegen aan toer als niet zowel installateur als datum zijn gekozen'
      );
      return;
    }
    this.loading = true;
    try {
      const result: AssignSubProjectResult =
        await this.assignSubProjectToTourControllerRequestService.assignSubProjectToTour(
          this.subProject.id,
          assignToCurrentSelection ? this.placer.id : null,
          assignToCurrentSelection ? moment(this.date) : null,
          assignToCurrentSelection ? this.locked : null,
          assignToCurrentSelection ? this.subProject.type : null
        );
      if (assignToCurrentSelection) {
        this.subProject.tourItem = new TourItem();
        this.subProject.tourItem.id = result.tourItemId;

        this.subProject.tourItem.tourRegistration = new LoggedAction();
        this.subProject.tourItem.tourRegistration.by =
          this.authService.getAuthenticatedUser();
        this.subProject.tourItem.tourRegistration.date = new Date();
      } else {
        this.subProject.tourItem = null;
        this.unsetTourConfiguration();
      }
      this.projectDisplayObservableService
        .getTourConfigurationChangedSubscribable()
        .next({
          assignedToTour: assignToCurrentSelection,
          subProjectId: this.subProject.id,
          placer: this.placer,
          date: this.date,
          locked: this.locked,
        });
      this.snackService.showSuccess('Toer succesvol aangepast');
    } finally {
      this.loading = false;
    }
  }

  compareEntities(o1: any, o2: any): boolean {
    return GeneralHelper.compareEntities(o1, o2);
  }
}
